import { StrictMode, type ComponentType, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import {
  createQueryClient,
  initPostHog,
  initSentry,
  loadMarkerWidget,
  SentryErrorBoundary,
} from '@loop3/observability'
import { ErrorFallback, toast } from '@loop3/ui'
import { composeWrappers } from './compose-wrappers'
import { assertClerkPublishableKey, resolveMountConfig, type MountL3AppEnv } from './mount-l3-app-config'

export interface MountL3AppOptions {
  /** Observability app tag ('timeoff', 'platform', …). */
  app: string
  App: ComponentType
  /** Message for the query client's global mutation-error toast. */
  mutationErrorMessage?: string
  /**
   * Extra providers between Clerk and QueryClientProvider — for providers
   * that only need Clerk context, not the query client (meetings:
   * EnsureActiveOrg). Applied innermost-first: the last entry sits closest
   * to QueryClientProvider. Deviation from the task-brief sketch, which
   * grouped this case into `wrappers` — see task report.
   */
  outerWrappers?: Array<ComponentType<{ children: ReactNode }>>
  /**
   * Extra providers between QueryClientProvider and <App/> (meetings:
   * InvalidateOnOrgChange; app/meetings: TooltipProvider). Applied
   * innermost-first: the last entry sits closest to <App/>.
   */
  wrappers?: Array<ComponentType<{ children: ReactNode }>>
  /**
   * Clerk config options. `allowedRedirectOrigins` is forwarded to
   * ClerkProvider in both router modes.
   */
  clerk?: { allowedRedirectOrigins?: string[] }
  posthog?: { disableAutocapture?: boolean }
  /** Custom Sentry fallback (platform/app uses its ErrorPage). Defaults to @loop3/ui ErrorFallback. */
  errorFallback?: ComponentType<{ error: unknown; resetError: () => void }>
  /**
   * True when <App/> renders its own top-level BrowserRouter (platform/app
   * today). mountL3App then skips wrapping BrowserRouter and mounts a plain
   * ClerkProvider (allowedRedirectOrigins only — no routerPush/Replace or
   * sign-in/up URL, since no Router context exists yet at Clerk's mount
   * point). Defaults to false. Deviation from the task-brief sketch, which
   * assumed BrowserRouter was universal — see task report.
   */
  appOwnsRouter?: boolean
}

const DEFAULT_MUTATION_ERROR_MESSAGE = "Couldn't save your change. Please try again."

function readEnv(): MountL3AppEnv {
  const env = import.meta.env
  return {
    mode: env.MODE,
    release: env.VITE_RELEASE,
    sentryDsn: env.VITE_SENTRY_DSN,
    posthogKey: env.VITE_POSTHOG_KEY,
    posthogHost: env.VITE_POSTHOG_HOST,
    markerProjectId: env.VITE_MARKER_PROJECT_ID,
    clerkPublishableKey: env.VITE_CLERK_PUBLISHABLE_KEY,
    clerkSignInUrl: env.VITE_CLERK_SIGN_IN_URL,
    clerkSignUpUrl: env.VITE_CLERK_SIGN_UP_URL,
  }
}

function DefaultErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  return <ErrorFallback error={error instanceof Error ? error : new Error(String(error))} resetError={resetError} />
}

/**
 * Boots an L3 Vite SPA: Sentry/PostHog/Marker init, the shared query client
 * (with a global mutation-error toast), Clerk key assertion, and the
 * `StrictMode > SentryErrorBoundary > BrowserRouter > Clerk > QueryClientProvider
 * > wrappers > <App/>` tree into `#root`. Replaces the near-identical
 * bootstrap duplicated across every app's `main.tsx`.
 */
export function mountL3App(options: MountL3AppOptions): void {
  const env = readEnv()
  const config = resolveMountConfig(options, env)

  // Sentry/PostHog/Marker all initialize once at module load, before the
  // Clerk key is validated — each helper no-ops safely when its key/DSN is
  // missing, so local dev works without any of them configured, and a
  // missing Clerk key still gets reported to Sentry if Sentry is live.
  initSentry(config.sentry)
  initPostHog(config.posthog)
  void loadMarkerWidget(config.marker)

  const queryClient = createQueryClient({
    notifyMutationError: () =>
      toast.error(options.mutationErrorMessage ?? DEFAULT_MUTATION_ERROR_MESSAGE),
  })

  const clerkPublishableKey = assertClerkPublishableKey(config.clerk.publishableKey)
  const Fallback = options.errorFallback ?? DefaultErrorFallback
  const App = options.App

  function ClerkProviderWithRouter({ children }: { children: ReactNode }) {
    const navigate = useNavigate()
    return (
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        routerPush={(to) => navigate(to)}
        routerReplace={(to) => navigate(to, { replace: true })}
        signInUrl={config.clerk.signInUrl}
        signUpUrl={config.clerk.signUpUrl}
        allowedRedirectOrigins={config.clerk.allowedRedirectOrigins}
      >
        {children}
      </ClerkProvider>
    )
  }

  function PlainClerkProvider({ children }: { children: ReactNode }) {
    return (
      <ClerkProvider publishableKey={clerkPublishableKey} allowedRedirectOrigins={config.clerk.allowedRedirectOrigins}>
        {children}
      </ClerkProvider>
    )
  }

  const body = composeWrappers(
    options.outerWrappers,
    <QueryClientProvider client={queryClient}>
      {composeWrappers(options.wrappers, <App />)}
    </QueryClientProvider>,
  )

  const tree = options.appOwnsRouter ? (
    <PlainClerkProvider>{body}</PlainClerkProvider>
  ) : (
    <BrowserRouter>
      <ClerkProviderWithRouter>{body}</ClerkProviderWithRouter>
    </BrowserRouter>
  )

  const root = document.getElementById('root')
  if (!root) throw new Error('Root element #root missing from index.html')

  createRoot(root).render(
    <StrictMode>
      <SentryErrorBoundary fallback={({ error, resetError }) => <Fallback error={error} resetError={resetError} />}>
        {tree}
      </SentryErrorBoundary>
    </StrictMode>,
  )
}
